import {
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Box,
    Button,
    CardContent,
    CircularProgress,
    Divider,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    Stack,
    Typography,
} from "@mui/material";

import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloseIcon from "@mui/icons-material/Close";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";

import { alpha } from "@mui/material/styles";

import { PRIMARY_COLOR } from "../util/constants";
import { handleResponse } from "../util/integration";
import {
    buyOfferAPI,
    fetchOfferAPI,
} from "../api/offer_api";
import { fetchPublicLeadAPI } from "../api/lead_api";
import {
    fetchUserCanBuylAPI,
    fetchUserProfileAPI,
} from "../api/user_api";
import {
    getIncreasingPollingDelay,
    getPollingDelay,
    POLLING_SPEED,
    usePolling,
} from "../util/polling";
import StaticMap from "../fragments/StaticMap";

const MemoizedStaticMap = memo(StaticMap);

const OFFER_STATUS = Object.freeze({
    created: "CREATED",
    active: "ACTIVE",
    terminated: "TERMINATED",
    aborted: "ABORTED",
});

const INFO_DIALOG = Object.freeze({
    details: "details",
    comment: "comment",
    documents: "documents",
    seller: "seller",
});

const VIEW_STATE = Object.freeze({
    loading: "loading",
    unavailable: "unavailable",
    ready: "ready",
});

const SELLER_VIEW_STATE = Object.freeze({
    loading: "loading",
    unavailable: "unavailable",
    ready: "ready",
});


const LABEL = Object.freeze({
    offerId: "ID",
    buyNow: "Jetzt kaufen",
    details: "Details",
    seller: "Anbieter",
    comment: "Kommentar",
    offer: "Sofortkauf",
    price: "Preis",
    documents: "Dokumente",
    offerInformation: "Angebotsdaten",
    sellerProfile: "Anbieterprofil",
    publicComment: "Öffentlicher Kommentar",
    pdfDocument: "PDF-Dokument",
    documentMissing: "Nicht hinterlegt",
    download: "Herunterladen",
    operatorTerms: "AGB des Betreibers",
    sellerTerms: "AGB des Anbieters",
    operatorPrivacyPolicy: "Datenschutzerklärung des Betreibers",
    sellerPrivacyPolicy: "Datenschutzerklärung des Anbieters",
    created: "Noch nicht gestartet",
    active: "Aktiv",
    terminated: "Beendet",
    aborted: "Abgebrochen",
    offerStart: "Angebotsstart",
    offerEnd: "Angebotsende",
    category: "Kategorie",
    itemType: "Art",
    zipCode: "Postleitzahl",
    status: "Status",
    b2b: "B2B",
    b2c: "B2C",
    sellerBusinessName: "Unternehmen",
    sellerName: "Name",
    sellerUsername: "Benutzername",
    sellerLocation: "Standort",
    sellerCountry: "Land",
    sellerProfileLoading: "Anbieterprofil wird geladen …",
    sellerProfileUnavailable: "Das Anbieterprofil ist nicht verfügbar.",
    closeDialog: "Dialog schließen",
    refreshOffer: "Angebot aktualisieren",
    defaultTitle: "Angebot",
    information: "Informationen",
});

const MESSAGE = Object.freeze({
    successfulPurchase: "Der Kauf wurde erfolgreich abgeschlossen.",
    failedPurchase: "Der Kaufversuch wurde nicht angenommen.",
    ownOffer: "Eigene Angebote können nicht gekauft werden.",
    noComment: "Kein Kommentar vorhanden.",
    documentUnavailable: "Das Dokument ist nicht verfügbar.",
    documentDownloadFailed: "Das Dokument konnte nicht heruntergeladen werden.",
    notAvailable: "Nicht verfügbar",
    missingLead: "Der zugehörige Lead konnte nicht ermittelt werden.",
    missingSeller: "Der Anbieter konnte nicht ermittelt werden.",
});

const CONFIG = Object.freeze({
    pollingMediumThresholdMs: 2 * 60 * 1_000,
    pollingFastThresholdMs: 20 * 1_000,
    defaultMapLatitude: 52.52,
    defaultMapLongitude: 13.405,
    mapZoom: 5,
    mapHeight: 150,
});

const VALUE = Object.freeze({
    empty: "",
    fallback: "–",
    documentFallback: "dokument",
    documentPrefix: "angebot",
    currencyCode: "EUR",
    currencyLocale: "de-DE",
    dateLocale: "de-DE",
    commentSeparator: "$",
    lineBreak: "\n",
    pdfMimeType: "application/pdf",
    downloadElement: "a",
});

const UI = Object.freeze({
    boolean: {
        true: true,
    },
    component: {
        span: "span",
        div: "div",
    },
    variant: {
        outlined: "outlined",
        caption: "caption",
        body1: "body1",
        body2: "body2",
        h5: "h5",
        h6: "h6",
        determinate: "determinate",
    },
    color: {
        secondaryText: "text.secondary",
        inactive: "gray",
    },
    size: {
        small: "small",
    },
    dialog: {
        scrollPaper: "paper",
        small: "sm",
        medium: "md",
    },
    grid: {
        full: 12,
        half: 6,
        contentSpacing: 3,
        detailsSpacing: 2.25,
    },
    stack: {
        row: "row",
        center: "center",
        spaceBetween: "space-between",
        cardSpacing: 1.25,
        documentSpacing: 2,
    },
    progress: {
        sellerSize: 22,
        offerSize: 12,
        offerThickness: 25,
        purchaseSize: 18,
    },
});

const RESPONSIVE_DOCUMENT_DIRECTION = Object.freeze({
    xs: "column",
    sm: "row",
});

const RESPONSIVE_DOCUMENT_ALIGNMENT = Object.freeze({
    xs: "stretch",
    sm: "center",
});

const DOCUMENT_DEFINITIONS = Object.freeze([
    {
        key: "operatorTerms",
        label: LABEL.operatorTerms,
        fileName: "betreiber-agb.pdf",
    },
    {
        key: "sellerTerms",
        label: LABEL.sellerTerms,
        fileName: "anbieter-agb.pdf",
    },
    {
        key: "operatorPrivacyPolicy",
        label: LABEL.operatorPrivacyPolicy,
        fileName: "betreiber-datenschutz.pdf",
    },
    {
        key: "sellerPrivacyPolicy",
        label: LABEL.sellerPrivacyPolicy,
        fileName: "anbieter-datenschutz.pdf",
    },
]);

const DIALOG_DEFINITIONS = Object.freeze({
    [INFO_DIALOG.details]: {
        title: LABEL.details,
        Icon: InfoOutlinedIcon,
        maxWidth: UI.dialog.medium,
    },
    [INFO_DIALOG.comment]: {
        title: LABEL.comment,
        Icon: CommentOutlinedIcon,
        maxWidth: UI.dialog.small,
    },
    [INFO_DIALOG.documents]: {
        title: LABEL.documents,
        Icon: DescriptionOutlinedIcon,
        maxWidth: UI.dialog.medium,
    },
    [INFO_DIALOG.seller]: {
        title: LABEL.seller,
        Icon: StorefrontOutlinedIcon,
        maxWidth: UI.dialog.medium,
    },
});

const DEFAULT_DIALOG_DEFINITION = Object.freeze({
    title: LABEL.information,
    Icon: InfoOutlinedIcon,
    maxWidth: UI.dialog.small,
});

const STYLE = Object.freeze({
    paper: {
        display: "block",
        width: "100%",
        height: "100%",
        minHeight: 320,
        overflow: "hidden",
        borderRadius: 3,
    },
    titleBar: {
        minHeight: "42px",
        padding: "20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        boxSizing: "border-box",
    },
    titleText: {
        fontFamily: "Arial, Helvetica",
        lineHeight: 1.25,
        minWidth: 0,
        wordBreak: "break-word",
    },
    badgeContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1,
        flexWrap: "wrap",
        flexShrink: 0,
    },
    headerBadge: {
        flexShrink: 0,
        padding: "5px 10px",
        borderRadius: "999px",
        fontSize: "0.75rem",
        lineHeight: 1.4,
        whiteSpace: "nowrap",
    },
    content: {
        padding: "25px",
    },
    actionContent: {
        padding: "25px",
        paddingTop: 0,
    },
    statusLabel: {
        fontSize: 14,
    },
    statusValue: {
        minHeight: "32px",
    },
    timeContainer: {
        marginTop: "5px",
    },
    progress: {
        marginLeft: "5px",
    },
    timeText: {
        display: "inline-block",
        marginLeft: "5px",
        fontSize: 14,
    },
    infoActions: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "8px",
    },
    infoButton: {
        color: "grey",
    },
    ownOfferMessage: {
        display: "block",
        marginTop: 1,
    },
    purchaseProgress: {
        marginLeft: 1,
    },
    dialogPaper: {
        borderRadius: 3,
        overflow: "hidden",
    },
    dialogTitle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: 3,
        py: 1.5,
    },
    dialogTitleContent: {
        minWidth: 0,
    },
    dialogTitleIcon: {
        display: "grid",
        placeItems: "center",
        width: 34,
        height: 34,
        borderRadius: 2,
        flexShrink: 0,
    },
    dialogTitleText: {
        fontFamily: "Arial, Helvetica",
        fontWeight: 700,
        overflowWrap: "anywhere",
    },
    dialogContent: {
        px: {
            xs: 2,
            sm: 3,
        },
        py: 3,
    },
    overviewPaper: {
        display: "block",
        overflow: "hidden",
        borderRadius: 3,
    },
    overviewContent: {
        padding: {
            xs: "18px",
            sm: "24px",
        },
        "&:last-child": {
            paddingBottom: {
                xs: "18px",
                sm: "24px",
            },
        },
    },
    cardHeader: {
        marginBottom: "18px",
    },
    cardIcon: {
        display: "grid",
        placeItems: "center",
        width: 34,
        height: 34,
        borderRadius: 2,
        flexShrink: 0,
    },
    cardTitle: {
        fontFamily: "Arial, Helvetica",
        fontSize: "1rem",
        fontWeight: 700,
    },
    infoLabel: {
        display: "block",
        marginBottom: "3px",
        fontWeight: 600,
    },
    infoValue: {
        overflowWrap: "anywhere",
        lineHeight: 1.45,
    },
    commentText: {
        margin: 0,
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        lineHeight: 1.65,
    },
    documentRow: {
        paddingTop: "10px",
        paddingBottom: "10px",
    },
    documentButton: {
        alignSelf: {
            xs: "stretch",
            sm: "center",
        },
        borderRadius: 2,
    },
    sellerLoading: {
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 2,
    },
});

const createColorStyles = (primaryColor) => Object.freeze({
    paper: {
        ...STYLE.paper,
        borderColor: alpha(primaryColor, 0.14),
        boxShadow: `0 12px 30px ${alpha(primaryColor, 0.06)}`,
    },
    titleBar: {
        ...STYLE.titleBar,
        backgroundColor: primaryColor,
        color: "white",
    },
    headerBadge: {
        ...STYLE.headerBadge,
        border: "1px solid rgba(255, 255, 255, 0.38)",
        backgroundColor: "rgba(255, 255, 255, 0.18)",
        color: "white",
    },
    dialogTitle: {
        ...STYLE.dialogTitle,
        backgroundColor: primaryColor,
        color: "white",
    },
    dialogCloseButton: {
        color: "white",
    },
    dialogTitleIcon: {
        ...STYLE.dialogTitleIcon,
        backgroundColor: "rgba(255, 255, 255, 0.18)",
        color: "white",
    },
    dialogContent: {
        ...STYLE.dialogContent,
        backgroundColor: alpha(primaryColor, 0.025),
    },
    overviewPaper: {
        ...STYLE.overviewPaper,
        borderColor: alpha(primaryColor, 0.14),
        boxShadow: `0 12px 30px ${alpha(primaryColor, 0.06)}`,
    },
    cardIcon: {
        ...STYLE.cardIcon,
        backgroundColor: alpha(primaryColor, 0.09),
        color: primaryColor,
    },
    actionButton: {
        color: primaryColor,
    },
    documentButton: {
        ...STYLE.documentButton,
        borderColor: primaryColor,
        color: primaryColor,
    },
    progress: {
        ...STYLE.progress,
        color: primaryColor,
    },
});

const COLOR_STYLE = createColorStyles(PRIMARY_COLOR);
const DIALOG_PAPER_PROPS = Object.freeze({ sx: STYLE.dialogPaper });

const normalizeCanBuy = (response) => {
    if (response !== null && typeof response === "object") {
        return Boolean(
            response.canBuy
            ?? response.allowed
            ?? response.value,
        );
    }

    return Boolean(response);
};

const displayValue = (value) => {
    if (value === null || value === undefined || value === VALUE.empty) {
        return VALUE.fallback;
    }

    return value;
};

const hasDocumentData = (value) => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }

    if (typeof value === "string") {
        return value.trim().length > 0;
    }

    return (
        value !== null
        && typeof value === "object"
        && Array.isArray(value.data)
        && value.data.length > 0
    );
};

const normalizeDocumentBytes = (value) => {
    if (Array.isArray(value)) {
        return new Uint8Array(value);
    }

    if (
        value !== null
        && typeof value === "object"
        && Array.isArray(value.data)
    ) {
        return new Uint8Array(value.data);
    }

    if (typeof value !== "string") {
        return null;
    }

    const base64Value = value
        .replace(/^data:[^;]+;base64,/, VALUE.empty)
        .replaceAll(/\s/g, VALUE.empty)
        .replaceAll("-", "+")
        .replaceAll("_", "/");

    if (base64Value === VALUE.empty) {
        return null;
    }

    const paddedBase64 = base64Value.padEnd(
        Math.ceil(base64Value.length / 4) * 4,
        "=",
    );

    try {
        const binaryValue = window.atob(paddedBase64);

        return Uint8Array.from(
            binaryValue,
            (character) => character.charCodeAt(0),
        );
    } catch {
        return null;
    }
};

const downloadDocument = (value, fileName) => {
    const documentBytes = normalizeDocumentBytes(value);

    if (documentBytes === null || documentBytes.length === 0) {
        throw new Error(MESSAGE.documentUnavailable);
    }

    const objectUrl = window.URL.createObjectURL(
        new Blob(
            [documentBytes],
            { type: VALUE.pdfMimeType },
        ),
    );

    const downloadLink = document.createElement(VALUE.downloadElement);
    downloadLink.href = objectUrl;
    downloadLink.download = fileName;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();

    window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
    }, 0);
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === VALUE.empty) {
        return VALUE.fallback;
    }

    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
        return `${value} ${VALUE.currencyCode}`;
    }

    return new Intl.NumberFormat(
        VALUE.currencyLocale,
        {
            style: "currency",
            currency: VALUE.currencyCode,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        },
    ).format(numericValue);
};

const formatDateTime = (value) => {
    if (value === null || value === undefined || value === VALUE.empty) {
        return VALUE.fallback;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return new Intl.DateTimeFormat(
        VALUE.dateLocale,
        {
            dateStyle: "medium",
            timeStyle: "medium",
        },
    ).format(date);
};

const formatComment = (comment) => {
    if (comment === null || comment === undefined || comment === VALUE.empty) {
        return MESSAGE.noComment;
    }

    return String(comment).replaceAll(
        VALUE.commentSeparator,
        VALUE.lineBreak,
    );
};

const formatSellerName = (sellerProfile) => {
    const name = [
        sellerProfile?.firstName,
        sellerProfile?.lastName,
    ]
        .filter(Boolean)
        .join(" ");

    return displayValue(name);
};

const formatSellerLocation = (sellerProfile) => {
    const location = [
        sellerProfile?.zipCode,
        sellerProfile?.town,
    ]
        .filter(Boolean)
        .join(" ");

    return displayValue(location);
};

const normalizeCoordinate = (value, minimum, maximum) => {
    if (value === null || value === undefined || value === VALUE.empty) {
        return null;
    }

    const numericValue = Number(value);

    if (
        !Number.isFinite(numericValue)
        || numericValue < minimum
        || numericValue > maximum
    ) {
        return null;
    }

    return numericValue;
};

const getMapCoordinates = (lead) => {
    const latitude = normalizeCoordinate(lead?.latitude, -90, 90);
    const longitude = normalizeCoordinate(lead?.longitude, -180, 180);

    if (latitude === null || longitude === null) {
        return {
            latitude: CONFIG.defaultMapLatitude,
            longitude: CONFIG.defaultMapLongitude,
        };
    }

    return { latitude, longitude };
};

const toTimestamp = (value) => {
    const numericValue = Number(value);

    if (Number.isFinite(numericValue)) {
        return numericValue;
    }

    const parsedValue = new Date(value).getTime();

    if (Number.isNaN(parsedValue)) {
        return null;
    }

    return parsedValue;
};

const getOfferEndTimestamp = (offer) => {
    const startTimestamp = toTimestamp(offer?.startTimestamp);
    const duration = Number(offer?.duration);

    if (startTimestamp === null || !Number.isFinite(duration)) {
        return null;
    }

    return startTimestamp + duration;
};

const getOfferPollingDelay = ({
    offer,
    pollingStartedAt,
}) => {
    const status = offer?.status ?? null;

    if (
        status === OFFER_STATUS.terminated
        || status === OFFER_STATUS.aborted
    ) {
        return null;
    }

    if (status !== OFFER_STATUS.active) {
        return getPollingDelay(POLLING_SPEED.slow);
    }

    const endTimestamp = getOfferEndTimestamp(offer);

    if (endTimestamp === null) {
        return getPollingDelay(POLLING_SPEED.slow);
    }

    return getIncreasingPollingDelay({
        endTimestamp,
        mediumThresholdMs: CONFIG.pollingMediumThresholdMs,
        fastThresholdMs: CONFIG.pollingFastThresholdMs,
        pollingStartedAt,
    });
};

const calculateProgress = (startTimestamp, duration) => {
    const normalizedStart = toTimestamp(startTimestamp);
    const normalizedDuration = Number(duration);

    if (
        normalizedStart === null
        || !Number.isFinite(normalizedDuration)
        || normalizedDuration <= 0
    ) {
        return 0;
    }

    const elapsed = Date.now() - normalizedStart;

    if (elapsed <= 0) {
        return 0;
    }

    if (elapsed >= normalizedDuration) {
        return 100;
    }

    return Math.round((elapsed / normalizedDuration) * 100);
};

const getCategoryName = (lead) => (
    lead?.category?.name
    ?? lead?.categoryName
    ?? null
);

const idsAreEqual = (firstId, secondId) => {
    if (
        firstId === null
        || firstId === undefined
        || secondId === null
        || secondId === undefined
    ) {
        return false;
    }

    return String(firstId) === String(secondId);
};

const buildOfferTitle = (lead) => {
    const categoryName = getCategoryName(lead);
    const zipCode = lead?.zipCode;
    const hasZipCode = zipCode !== null
        && zipCode !== undefined
        && zipCode !== VALUE.empty;
    const hasCategoryName = categoryName !== null
        && categoryName !== undefined
        && categoryName !== VALUE.empty;

    if (hasZipCode && hasCategoryName) {
        return `${zipCode}: ${categoryName}`;
    }

    return categoryName ?? zipCode ?? LABEL.defaultTitle;
};

const getStatusLabel = (status) => {
    switch (status) {
        case OFFER_STATUS.created:
            return LABEL.created;
        case OFFER_STATUS.active:
            return LABEL.active;
        case OFFER_STATUS.terminated:
            return LABEL.terminated;
        case OFFER_STATUS.aborted:
            return LABEL.aborted;
        default:
            return MESSAGE.notAvailable;
    }
};

const getStatusDisplayValue = (offer, bought) => {
    if (bought) {
        return LABEL.terminated;
    }

    switch (offer?.status) {
        case OFFER_STATUS.created:
            return LABEL.created;
        case OFFER_STATUS.active:
            return formatCurrency(offer.price);
        case OFFER_STATUS.terminated:
            return LABEL.terminated;
        case OFFER_STATUS.aborted:
            return LABEL.aborted;
        default:
            return MESSAGE.notAvailable;
    }
};

const getStatusTimeText = (offer) => {
    if (offer?.status === OFFER_STATUS.created) {
        return `${LABEL.offerStart}: ${formatDateTime(offer.startTimestamp)}`;
    }

    if (offer?.status === OFFER_STATUS.active) {
        return `${LABEL.offerEnd}: ${formatDateTime(getOfferEndTimestamp(offer))}`;
    }

    return VALUE.empty;
};

const getLeadTypeLabel = (lead) => {
    if (lead?.type === LABEL.b2b) {
        return LABEL.b2b;
    }

    if (lead?.type === LABEL.b2c) {
        return LABEL.b2c;
    }

    return displayValue(lead?.type);
};

const buildDocumentFilePrefix = (offerId) => (
    `${VALUE.documentPrefix}-${offerId ?? VALUE.documentFallback}`
);

const buildDocumentFileName = (filePrefix, fileName) => (
    `${filePrefix}-${fileName}`
);

const useMountedRef = () => {
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;

        return () => {
            isMounted.current = false;
        };
    }, []);

    return isMounted;
};

const useOfferResource = ({
    accessToken,
    offerId,
    userId,
    showMessage,
}) => {
    const isMounted = useMountedRef();
    const [offer, setOffer] = useState(null);
    const [lead, setLead] = useState(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isLeadLoading, setIsLeadLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [hasOfferError, setHasOfferError] = useState(false);
    const [hasLeadError, setHasLeadError] = useState(false);
    const currentOfferRef = useRef(null);
    const currentOfferIdRef = useRef(String(offerId));
    const requestInProgress = useRef(null);

    const loadOffer = useCallback((options = {}) => {
        const {
            initialLoad = false,
            showRefreshing = false,
            silentError = false,
        } = options;
        const requestOfferId = String(offerId);

        return new Promise((resolve) => {
            const finishRequest = () => {
                if (requestInProgress.current === requestOfferId) {
                    requestInProgress.current = null;
                }

                resolve();
            };

            if (requestInProgress.current === requestOfferId) {
                if (showRefreshing) {
                    setIsRefreshing(false);
                }

                resolve();
                return;
            }

            requestInProgress.current = requestOfferId;

            if (initialLoad) {
                setIsInitialLoading(true);
            }

            if (showRefreshing) {
                setIsRefreshing(true);
            }

            handleResponse(
                fetchOfferAPI(accessToken, offerId, userId),
                (response) => {
                    if (
                        !isMounted.current
                        || currentOfferIdRef.current !== requestOfferId
                    ) {
                        finishRequest();
                        return;
                    }

                    currentOfferRef.current = response;
                    setOffer(response);
                    setHasOfferError(false);
                    setIsInitialLoading(false);
                    setIsRefreshing(false);
                    finishRequest();
                },
                (message) => {
                    if (
                        !isMounted.current
                        || currentOfferIdRef.current !== requestOfferId
                    ) {
                        finishRequest();
                        return;
                    }

                    if (initialLoad) {
                        currentOfferRef.current = null;
                        setOffer(null);
                        setHasOfferError(true);
                    }

                    setIsInitialLoading(false);
                    setIsRefreshing(false);

                    if (!silentError) {
                        showMessage(message);
                    }

                    finishRequest();
                },
            );
        });
    }, [accessToken, isMounted, offerId, showMessage, userId]);

    useEffect(() => {
        currentOfferIdRef.current = String(offerId);
        currentOfferRef.current = null;
        setOffer(null);
        setLead(null);
        setHasOfferError(false);
        setHasLeadError(false);
        setIsInitialLoading(true);
        setIsLeadLoading(false);
        setIsRefreshing(false);

        void loadOffer({ initialLoad: true });
    }, [loadOffer, offerId]);

    const leadId = offer?.leadId ?? null;

    useEffect(() => {
        if (leadId === null || leadId === undefined) {
            if (currentOfferRef.current !== null) {
                setLead(null);
                setHasLeadError(true);
                setIsLeadLoading(false);
                showMessage(MESSAGE.missingLead);
            }

            return undefined;
        }

        let requestIsActive = true;

        setLead(null);
        setHasLeadError(false);
        setIsLeadLoading(true);

        handleResponse(
            fetchPublicLeadAPI(accessToken, leadId),
            (response) => {
                if (!requestIsActive) {
                    return;
                }

                setLead(response);
                setIsLeadLoading(false);
            },
            (message) => {
                if (!requestIsActive) {
                    return;
                }

                setLead(null);
                setHasLeadError(true);
                setIsLeadLoading(false);
                showMessage(message);
            },
        );

        return () => {
            requestIsActive = false;
        };
    }, [accessToken, leadId, showMessage]);

    const shouldPoll = offer !== null
        && (
            offer.status === null
            || offer.status === undefined
            || offer.status === OFFER_STATUS.created
            || offer.status === OFFER_STATUS.active
        );

    usePolling({
        enabled: shouldPoll,
        restartKey: offerId,
        poll: () => loadOffer({ silentError: true }),
        getDelay: ({ pollingStartedAt }) => getOfferPollingDelay({
            offer: currentOfferRef.current,
            pollingStartedAt,
        }),
    });

    const refresh = useCallback(() => {
        if (isRefreshing) {
            return;
        }

        void loadOffer({ showRefreshing: true });
    }, [isRefreshing, loadOffer]);

    return {
        offer,
        lead,
        isLoading: isInitialLoading || isLeadLoading,
        isRefreshing,
        hasOfferError,
        hasLeadError,
        loadOffer,
        refresh,
    };
};

const useBuyerPermission = ({
    userId,
    username,
    accessToken,
    showMessage,
}) => {
    const [canBuy, setCanBuy] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let requestIsActive = true;

        setIsLoading(true);

        handleResponse(
            fetchUserCanBuylAPI(userId, username, accessToken),
            (response) => {
                if (!requestIsActive) {
                    return;
                }

                setCanBuy(normalizeCanBuy(response));
                setIsLoading(false);
            },
            (message) => {
                if (!requestIsActive) {
                    return;
                }

                setCanBuy(false);
                setIsLoading(false);
                showMessage(message);
            },
        );

        return () => {
            requestIsActive = false;
        };
    }, [accessToken, showMessage, userId, username]);

    return {
        canBuy,
        isLoading,
    };
};

const useSellerProfileController = ({
    accessToken,
    sellerId,
    openDialog,
    showMessage,
}) => {
    const [sellerProfile, setSellerProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (openDialog !== INFO_DIALOG.seller) {
            return undefined;
        }

        if (sellerId === null || sellerId === undefined) {
            setSellerProfile(null);
            setHasError(true);
            setIsLoading(false);
            showMessage(MESSAGE.missingSeller);
            return undefined;
        }

        let requestIsActive = true;

        setSellerProfile(null);
        setHasError(false);
        setIsLoading(true);

        handleResponse(
            fetchUserProfileAPI(sellerId, accessToken),
            (response) => {
                if (!requestIsActive) {
                    return;
                }

                setSellerProfile(response);
                setIsLoading(false);
            },
            (message) => {
                if (!requestIsActive) {
                    return;
                }

                setSellerProfile(null);
                setHasError(true);
                setIsLoading(false);
                showMessage(message);
            },
        );

        return () => {
            requestIsActive = false;
        };
    }, [accessToken, openDialog, sellerId, showMessage]);

    return {
        sellerProfile,
        isLoading,
        hasError,
    };
};

const useDocumentDownload = (showMessage) => useCallback((value, fileName) => {
    try {
        downloadDocument(value, fileName);
    } catch (error) {
        showMessage(error?.message || MESSAGE.documentDownloadFailed);
    }
}, [showMessage]);

const usePurchaseController = ({
    accessToken,
    offer,
    offerId,
    userId,
    canBuy,
    canBuyLoading,
    isOwnOffer,
    loadOffer,
    reload,
    showMessage,
}) => {
    const [bought, setBought] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setBought(false);
        setIsLoading(false);
    }, [offerId]);

    const isOfferActive = offer?.status === OFFER_STATUS.active && !bought;
    const disabled = !isOfferActive
        || isOwnOffer
        || !canBuy
        || canBuyLoading
        || isLoading;

    const invokeReload = useCallback(() => {
        if (typeof reload === "function") {
            reload();
        }
    }, [reload]);

    const buy = useCallback(() => {
        if (
            offer === null
            || bought
            || isOwnOffer
            || !isOfferActive
            || !canBuy
            || canBuyLoading
            || isLoading
        ) {
            return;
        }

        setIsLoading(true);

        handleResponse(
            buyOfferAPI(accessToken, offer.id, userId),
            (success) => {
                setIsLoading(false);

                if (success) {
                    setBought(true);
                    showMessage(MESSAGE.successfulPurchase);
                    void loadOffer();
                    invokeReload();
                    return;
                }

                showMessage(MESSAGE.failedPurchase);
                void loadOffer({ showRefreshing: true });
                invokeReload();
            },
            (message) => {
                setIsLoading(false);
                showMessage(message);
                void loadOffer({ silentError: true });
            },
        );
    }, [
        accessToken,
        bought,
        canBuy,
        canBuyLoading,
        invokeReload,
        isLoading,
        isOfferActive,
        isOwnOffer,
        loadOffer,
        offer,
        showMessage,
        userId,
    ]);

    return {
        bought,
        isLoading,
        isOfferActive,
        disabled,
        buy,
    };
};

const useOfferDialogController = ({
    offer,
    offerId,
    lead,
    sellerId,
    accessToken,
    showMessage,
    handleDocumentDownload,
}) => {
    const [openDialog, setOpenDialog] = useState(null);
    const seller = useSellerProfileController({
        accessToken,
        sellerId,
        openDialog,
        showMessage,
    });

    useEffect(() => {
        setOpenDialog(null);
    }, [offerId]);

    const openDetails = useCallback(() => {
        setOpenDialog(INFO_DIALOG.details);
    }, []);

    const openComment = useCallback(() => {
        setOpenDialog(INFO_DIALOG.comment);
    }, []);

    const openDocuments = useCallback(() => {
        setOpenDialog(INFO_DIALOG.documents);
    }, []);

    const openSeller = useCallback(() => {
        setOpenDialog(INFO_DIALOG.seller);
    }, []);

    const close = useCallback(() => {
        setOpenDialog(null);
    }, []);

    const documentFilePrefix = buildDocumentFilePrefix(offer?.id);

    const documents = useMemo(() => DOCUMENT_DEFINITIONS.map((definition) => {
        const value = offer?.[definition.key];
        const fileName = buildDocumentFileName(
            documentFilePrefix,
            definition.fileName,
        );
        const available = hasDocumentData(value);

        return {
            key: definition.key,
            title: definition.label,
            disabled: !available,
            typeText: available ? LABEL.pdfDocument : LABEL.documentMissing,
            downloadLabel: LABEL.download,
            onDownload: () => handleDocumentDownload(value, fileName),
        };
    }), [documentFilePrefix, handleDocumentDownload, offer]);

    const details = useMemo(() => ({
        title: LABEL.offerInformation,
        Icon: ShoppingCartOutlinedIcon,
        rows: [
            {
                key: LABEL.offerId,
                label: LABEL.offerId,
                value: displayValue(offer?.id),
                sm: UI.grid.full,
            },
            {
                key: LABEL.category,
                label: LABEL.category,
                value: displayValue(getCategoryName(lead)),
                sm: UI.grid.full,
            },
            {
                key: LABEL.itemType,
                label: LABEL.itemType,
                value: getLeadTypeLabel(lead),
                sm: UI.grid.half,
            },
            {
                key: LABEL.zipCode,
                label: LABEL.zipCode,
                value: displayValue(lead?.zipCode),
                sm: UI.grid.half,
            },
            {
                key: LABEL.status,
                label: LABEL.status,
                value: getStatusLabel(offer?.status),
                sm: UI.grid.half,
            },
            {
                key: LABEL.price,
                label: LABEL.price,
                value: formatCurrency(offer?.price),
                sm: UI.grid.half,
            },
            {
                key: LABEL.offerStart,
                label: LABEL.offerStart,
                value: formatDateTime(offer?.startTimestamp),
                sm: UI.grid.half,
            },
            {
                key: LABEL.offerEnd,
                label: LABEL.offerEnd,
                value: formatDateTime(getOfferEndTimestamp(offer)),
                sm: UI.grid.half,
            },
        ],
    }), [lead, offer]);

    const comment = useMemo(() => ({
        title: LABEL.publicComment,
        Icon: CommentOutlinedIcon,
        text: formatComment(lead?.publicComment),
    }), [lead]);

    const sellerViewState = seller.isLoading
        ? SELLER_VIEW_STATE.loading
        : seller.hasError || seller.sellerProfile === null
            ? SELLER_VIEW_STATE.unavailable
            : SELLER_VIEW_STATE.ready;

    const sellerView = useMemo(() => ({
        title: LABEL.sellerProfile,
        Icon: StorefrontOutlinedIcon,
        state: sellerViewState,
        loadingText: LABEL.sellerProfileLoading,
        unavailableText: LABEL.sellerProfileUnavailable,
        rows: seller.sellerProfile === null
            ? []
            : [
                {
                    key: LABEL.sellerBusinessName,
                    label: LABEL.sellerBusinessName,
                    value: displayValue(
                        seller.sellerProfile.businessName
                        ?? seller.sellerProfile.firm,
                    ),
                    sm: UI.grid.full,
                },
                {
                    key: LABEL.sellerName,
                    label: LABEL.sellerName,
                    value: formatSellerName(seller.sellerProfile),
                    sm: UI.grid.half,
                },
                {
                    key: LABEL.sellerUsername,
                    label: LABEL.sellerUsername,
                    value: displayValue(seller.sellerProfile.username),
                    sm: UI.grid.half,
                },
                {
                    key: LABEL.sellerLocation,
                    label: LABEL.sellerLocation,
                    value: formatSellerLocation(seller.sellerProfile),
                    sm: UI.grid.half,
                },
                {
                    key: LABEL.sellerCountry,
                    label: LABEL.sellerCountry,
                    value: displayValue(seller.sellerProfile.country),
                    sm: UI.grid.half,
                },
            ],
    }), [seller.sellerProfile, sellerViewState]);

    const definition = DIALOG_DEFINITIONS[openDialog] ?? DEFAULT_DIALOG_DEFINITION;

    return {
        dialog: {
            open: openDialog !== null,
            type: openDialog,
            title: definition.title,
            Icon: definition.Icon,
            maxWidth: definition.maxWidth,
            closeLabel: LABEL.closeDialog,
            onClose: close,
            details,
            comment,
            documents: {
                title: LABEL.documents,
                Icon: DescriptionOutlinedIcon,
                rows: documents,
            },
            seller: sellerView,
        },
        actions: [
            {
                key: INFO_DIALOG.details,
                label: LABEL.details,
                Icon: InfoOutlinedIcon,
                onClick: openDetails,
            },
            {
                key: INFO_DIALOG.comment,
                label: LABEL.comment,
                Icon: CommentOutlinedIcon,
                onClick: openComment,
            },
            {
                key: INFO_DIALOG.documents,
                label: LABEL.documents,
                Icon: DescriptionOutlinedIcon,
                onClick: openDocuments,
            },
            {
                key: INFO_DIALOG.seller,
                label: LABEL.seller,
                Icon: StorefrontOutlinedIcon,
                onClick: openSeller,
            },
        ],
    };
};

const useOfferViewModel = ({
    appAPI,
    contextAPI,
    offerId,
    reload,
}) => {
    const accessToken = contextAPI.password;
    const username = contextAPI.username;
    const userId = contextAPI.id;
    const showMessage = appAPI.showMessage;

    const resource = useOfferResource({
        accessToken,
        offerId,
        userId,
        showMessage,
    });
    const buyerPermission = useBuyerPermission({
        userId,
        username,
        accessToken,
        showMessage,
    });
    const handleDocumentDownload = useDocumentDownload(showMessage);
    const sellerId = resource.lead?.authorId ?? null;
    const isOwnOffer = idsAreEqual(sellerId, userId);
    const purchase = usePurchaseController({
        accessToken,
        offer: resource.offer,
        offerId,
        userId,
        canBuy: buyerPermission.canBuy,
        canBuyLoading: buyerPermission.isLoading,
        isOwnOffer,
        loadOffer: resource.loadOffer,
        reload,
        showMessage,
    });
    const dialogController = useOfferDialogController({
        offer: resource.offer,
        offerId,
        lead: resource.lead,
        sellerId,
        accessToken,
        showMessage,
        handleDocumentDownload,
    });

    const viewState = resource.isLoading
        ? VIEW_STATE.loading
        : resource.hasOfferError
            || resource.hasLeadError
            || resource.offer === null
            || resource.lead === null
            ? VIEW_STATE.unavailable
            : VIEW_STATE.ready;
    const mapCoordinates = getMapCoordinates(resource.lead);
    const statusColor = purchase.isOfferActive
        ? PRIMARY_COLOR
        : UI.color.inactive;

    return {
        state: viewState,
        loading: {},
        unavailable: {
            message: MESSAGE.notAvailable,
        },
        ready: {
            map: {
                latitude: mapCoordinates.latitude,
                longitude: mapCoordinates.longitude,
                zoom: CONFIG.mapZoom,
                height: CONFIG.mapHeight,
            },
            header: {
                title: buildOfferTitle(resource.lead),
                statusLabel: purchase.bought
                    ? LABEL.terminated
                    : getStatusLabel(resource.offer?.status),
                typeLabel: LABEL.offer,
            },
            status: {
                label: LABEL.offer,
                valueStyle: {
                    ...STYLE.statusValue,
                    color: statusColor,
                },
                value: getStatusDisplayValue(resource.offer, purchase.bought),
                showProgress: purchase.isOfferActive,
                progress: calculateProgress(
                    resource.offer?.startTimestamp,
                    resource.offer?.duration,
                ),
                timeText: getStatusTimeText(resource.offer),
            },
            actions: {
                info: dialogController.actions,
                refresh: {
                    label: LABEL.refreshOffer,
                    disabled: resource.isRefreshing,
                    onClick: resource.refresh,
                },
                purchase: {
                    visible: !purchase.bought,
                    label: LABEL.buyNow,
                    disabled: purchase.disabled,
                    onClick: purchase.buy,
                },
                purchaseProgress: {
                    visible: purchase.isLoading,
                },
                ownOffer: {
                    visible: isOwnOffer,
                    text: MESSAGE.ownOffer,
                },
            },
            dialog: dialogController.dialog,
        },
    };
};

function InfoRow({ model }) {
    return (
        <Grid
            item={UI.boolean.true}
            xs={UI.grid.full}
            sm={model.sm}
        >
            <Typography
                variant={UI.variant.caption}
                color={UI.color.secondaryText}
                sx={STYLE.infoLabel}
            >
                {model.label}
            </Typography>

            <Typography
                variant={UI.variant.body1}
                sx={STYLE.infoValue}
            >
                {model.value}
            </Typography>
        </Grid>
    );
}

function InfoRows({ rows }) {
    return rows.map((row) => (
        <InfoRow
            key={row.key}
            model={row}
        />
    ));
}

function OverviewCard({ model, children }) {
    const Icon = model.Icon;

    return (
        <Paper
            variant={UI.variant.outlined}
            sx={COLOR_STYLE.overviewPaper}
        >
            <CardContent sx={STYLE.overviewContent}>
                <Stack
                    direction={UI.stack.row}
                    alignItems={UI.stack.center}
                    spacing={UI.stack.cardSpacing}
                    sx={STYLE.cardHeader}
                >
                    <Box sx={COLOR_STYLE.cardIcon}>
                        <Icon fontSize={UI.size.small} />
                    </Box>

                    <Typography
                        variant={UI.variant.h6}
                        sx={STYLE.cardTitle}
                    >
                        {model.title}
                    </Typography>
                </Stack>

                {children}
            </CardContent>
        </Paper>
    );
}

function OfferDetailsView({ model }) {
    return (
        <OverviewCard model={model}>
            <Grid
                container={UI.boolean.true}
                spacing={UI.grid.detailsSpacing}
            >
                <InfoRows rows={model.rows} />
            </Grid>
        </OverviewCard>
    );
}

function CommentDetailsView({ model }) {
    return (
        <OverviewCard model={model}>
            <Typography
                variant={UI.variant.body1}
                component={UI.component.div}
                sx={STYLE.commentText}
            >
                {model.text}
            </Typography>
        </OverviewCard>
    );
}

function DocumentRow({ model }) {
    return (
        <Stack
            direction={RESPONSIVE_DOCUMENT_DIRECTION}
            alignItems={RESPONSIVE_DOCUMENT_ALIGNMENT}
            justifyContent={UI.stack.spaceBetween}
            spacing={UI.stack.documentSpacing}
            sx={STYLE.documentRow}
        >
            <Box>
                <Typography fontWeight={STYLE.cardTitle.fontWeight}>
                    {model.title}
                </Typography>

                <Typography
                    variant={UI.variant.body2}
                    color={UI.color.secondaryText}
                >
                    {model.typeText}
                </Typography>
            </Box>

            <Button
                variant={UI.variant.outlined}
                startIcon={<DownloadOutlinedIcon />}
                disabled={model.disabled}
                onClick={model.onDownload}
                sx={COLOR_STYLE.documentButton}
            >
                {model.downloadLabel}
            </Button>
        </Stack>
    );
}

const renderDocumentRow = (row) => (
    <DocumentRow
        key={row.key}
        model={row}
    />
);

function DocumentRows({ rows }) {
    const renderedRows = rows.map(renderDocumentRow);

    return (
        <Stack divider={<Divider />}>
            {renderedRows}
        </Stack>
    );
}

function DocumentDownloadsView({ model }) {
    return (
        <OverviewCard model={model}>
            <DocumentRows rows={model.rows} />
        </OverviewCard>
    );
}

function SellerLoadingView({ model }) {
    return (
        <Box sx={STYLE.sellerLoading}>
            <CircularProgress size={UI.progress.sellerSize} />
            <Typography color={UI.color.secondaryText}>
                {model.loadingText}
            </Typography>
        </Box>
    );
}

function SellerUnavailableView({ model }) {
    return (
        <Typography color={UI.color.secondaryText}>
            {model.unavailableText}
        </Typography>
    );
}

function SellerReadyView({ model }) {
    return (
        <Grid
            container={UI.boolean.true}
            spacing={UI.grid.detailsSpacing}
        >
            <InfoRows rows={model.rows} />
        </Grid>
    );
}

const SELLER_VIEW_COMPONENT = Object.freeze({
    [SELLER_VIEW_STATE.loading]: SellerLoadingView,
    [SELLER_VIEW_STATE.unavailable]: SellerUnavailableView,
    [SELLER_VIEW_STATE.ready]: SellerReadyView,
});

function SellerProfileView({ model }) {
    const Content = SELLER_VIEW_COMPONENT[model.state];

    return (
        <OverviewCard model={model}>
            <Content model={model} />
        </OverviewCard>
    );
}

const DIALOG_CONTENT_COMPONENT = Object.freeze({
    [INFO_DIALOG.details]: OfferDetailsView,
    [INFO_DIALOG.comment]: CommentDetailsView,
    [INFO_DIALOG.documents]: DocumentDownloadsView,
    [INFO_DIALOG.seller]: SellerProfileView,
});

const DIALOG_CONTENT_MODEL_KEY = Object.freeze({
    [INFO_DIALOG.details]: INFO_DIALOG.details,
    [INFO_DIALOG.comment]: INFO_DIALOG.comment,
    [INFO_DIALOG.documents]: INFO_DIALOG.documents,
    [INFO_DIALOG.seller]: INFO_DIALOG.seller,
});

function OfferDialogContent({ model }) {
    const Content = DIALOG_CONTENT_COMPONENT[model.type] ?? OfferDetailsView;
    const modelKey = DIALOG_CONTENT_MODEL_KEY[model.type] ?? INFO_DIALOG.details;
    const contentModel = model[modelKey];

    return (
        <Content model={contentModel} />
    );
}

function OfferInfoDialog({ model }) {
    const Icon = model.Icon;

    return (
        <Dialog
            open={model.open}
            onClose={model.onClose}
            fullWidth={UI.boolean.true}
            maxWidth={model.maxWidth}
            scroll={UI.dialog.scrollPaper}
            PaperProps={DIALOG_PAPER_PROPS}
        >
            <DialogTitle sx={COLOR_STYLE.dialogTitle}>
                <Stack
                    direction={UI.stack.row}
                    alignItems={UI.stack.center}
                    spacing={UI.stack.cardSpacing}
                    sx={STYLE.dialogTitleContent}
                >
                    <Box sx={COLOR_STYLE.dialogTitleIcon}>
                        <Icon fontSize={UI.size.small} />
                    </Box>

                    <Typography
                        component={UI.component.span}
                        sx={STYLE.dialogTitleText}
                    >
                        {model.title}
                    </Typography>
                </Stack>

                <IconButton
                    onClick={model.onClose}
                    aria-label={model.closeLabel}
                    sx={COLOR_STYLE.dialogCloseButton}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                dividers={UI.boolean.true}
                sx={COLOR_STYLE.dialogContent}
            >
                <OfferDialogContent model={model} />
            </DialogContent>
        </Dialog>
    );
}

function OfferHeader({ model }) {
    return (
        <Box sx={COLOR_STYLE.titleBar}>
            <Typography
                component={UI.component.span}
                sx={STYLE.titleText}
            >
                {model.title}
            </Typography>

            <Box sx={STYLE.badgeContainer}>
                <Box
                    component={UI.component.span}
                    sx={COLOR_STYLE.headerBadge}
                >
                    {model.statusLabel}
                </Box>

                <Box
                    component={UI.component.span}
                    sx={COLOR_STYLE.headerBadge}
                >
                    {model.typeLabel}
                </Box>
            </Box>
        </Box>
    );
}

function OfferProgress({ model }) {
    if (!model.showProgress) {
        return null;
    }

    return (
        <CircularProgress
            sx={COLOR_STYLE.progress}
            value={model.progress}
            size={UI.progress.offerSize}
            thickness={UI.progress.offerThickness}
            variant={UI.variant.determinate}
        />
    );
}

function OfferStatus({ model }) {
    return (
        <CardContent sx={STYLE.content}>
            <Typography
                sx={STYLE.statusLabel}
                color={UI.color.secondaryText}
                gutterBottom={UI.boolean.true}
            >
                {model.label}
            </Typography>

            <Typography
                variant={UI.variant.h5}
                component={UI.component.div}
                sx={model.valueStyle}
            >
                {model.value}
            </Typography>

            <Box sx={STYLE.timeContainer}>
                <OfferProgress model={model} />

                <Typography
                    sx={STYLE.timeText}
                    color={UI.color.secondaryText}
                    gutterBottom={UI.boolean.true}
                >
                    {model.timeText}
                </Typography>
            </Box>
        </CardContent>
    );
}

function InfoActionButton({ model }) {
    const Icon = model.Icon;

    return (
        <Button
            size={UI.size.small}
            startIcon={<Icon />}
            sx={STYLE.infoButton}
            onClick={model.onClick}
        >
            {model.label}
        </Button>
    );
}

const renderInfoAction = (action) => (
    <InfoActionButton
        key={action.key}
        model={action}
    />
);

function InfoActions({ actions }) {
    const renderedActions = actions.map(renderInfoAction);

    return (
        <Box sx={STYLE.infoActions}>
            {renderedActions}
        </Box>
    );
}

function PurchaseButton({ model }) {
    if (!model.visible) {
        return null;
    }

    return (
        <Button
            startIcon={<ShoppingCartOutlinedIcon />}
            sx={COLOR_STYLE.actionButton}
            onClick={model.onClick}
            disabled={model.disabled}
        >
            {model.label}
        </Button>
    );
}

function PurchaseProgress({ model }) {
    if (!model.visible) {
        return null;
    }

    return (
        <CircularProgress
            size={UI.progress.purchaseSize}
            sx={STYLE.purchaseProgress}
        />
    );
}

function OwnOfferMessage({ model }) {
    if (!model.visible) {
        return null;
    }

    return (
        <Typography
            variant={UI.variant.caption}
            color={UI.color.secondaryText}
            sx={STYLE.ownOfferMessage}
        >
            {model.text}
        </Typography>
    );
}

function OfferButtons({ model }) {
    return (
        <>
            <IconButton
                sx={COLOR_STYLE.actionButton}
                onClick={model.refresh.onClick}
                disabled={model.refresh.disabled}
                aria-label={model.refresh.label}
            >
                <AutorenewIcon />
            </IconButton>

            <PurchaseButton model={model.purchase} />
            <PurchaseProgress model={model.purchaseProgress} />
            <OwnOfferMessage model={model.ownOffer} />
        </>
    );
}

function OfferActions({ model }) {
    return (
        <CardContent sx={STYLE.actionContent}>
            <Grid
                container={UI.boolean.true}
                spacing={UI.grid.contentSpacing}
            >
                <Grid
                    item={UI.boolean.true}
                    xs={UI.grid.full}
                >
                    <InfoActions actions={model.info} />
                </Grid>

                <Grid
                    item={UI.boolean.true}
                    xs={UI.grid.full}
                >
                    <OfferButtons model={model} />
                </Grid>
            </Grid>
        </CardContent>
    );
}

function LoadingView() {
    return (
        <LinearProgress />
    );
}

function UnavailableView({ model }) {
    return (
        <Typography>
            {model.message}
        </Typography>
    );
}

function ReadyView({ model }) {
    return (
        <>
            <Paper
                variant={UI.variant.outlined}
                sx={COLOR_STYLE.paper}
            >
                <MemoizedStaticMap
                    latitude={model.map.latitude}
                    longitude={model.map.longitude}
                    zoom={model.map.zoom}
                    height={model.map.height}
                />

                <OfferHeader model={model.header} />
                <OfferStatus model={model.status} />
                <OfferActions model={model.actions} />
            </Paper>

            <OfferInfoDialog model={model.dialog} />
        </>
    );
}

const PAGE_COMPONENT = Object.freeze({
    [VIEW_STATE.loading]: LoadingView,
    [VIEW_STATE.unavailable]: UnavailableView,
    [VIEW_STATE.ready]: ReadyView,
});

export default function Offer({
    appAPI,
    contextAPI,
    offerId,
    reload,
}) {
    const viewModel = useOfferViewModel({
        appAPI,
        contextAPI,
        offerId,
        reload,
    });
    const Page = PAGE_COMPONENT[viewModel.state];
    const pageModel = viewModel[viewModel.state];

    return (
        <Page model={pageModel} />
    );
}